import React, { useState, useEffect, useRef } from 'react';
import { Toast } from '../../../../../components/common/Toast';
import ConsentSelect from '../../ConsentSelect';
import ServiceDropdown from '../../ServiceDropdown';
import { DeleteIcon } from '../../../../../components/common/SvgIcon';
import ConfirmationModal from '../../../../../components/common/ConfirmationModal';

const AVAILABLE_SERVICES = [
  'AWIN',
  'Facebook Pixel',
  'Facebook Social Plugins',
  'Google Adsense',
  'Google AdWords Remarketing/Conversion Tag',
  'Google Analytics',
  'Google Gtag',
  'Google Maps',
  'Google Tag Manager',
  'Instagram',
  'Jetpack Stats',
  'LinkedIn Pixel',
  'Matomo',
  'Twitter',
  'Vimeo',
  'YouTube',
];

const CONSENT_OPTIONS = [
  { value: 'allowed_with_consent', label: 'Allowed with consent' },
  { value: 'always_blocked',       label: 'Always blocked' },
  { value: 'always_allowed',       label: 'Always allowed' },
];

const RulesTab = () => {
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    ruleId: null,
    isLoading: false,
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const res = await fetch(`${LEGAL_PAGES.apiUrl}/cookie-block-rules`, {
        headers: { 'X-WP-Nonce': LEGAL_PAGES.nonce },
      });
      const result = await res.json();
      if (result.success && Array.isArray(result.data)) {
        setRules(result.data);
      }
    } catch (e) {
      // silently fail — start with empty
    }
  };

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      { id: Date.now(), service: '', consent: 'allowed_with_consent' },
    ]);
  };

  const updateRule = (id, field, value) => {
    setRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const removeRule = (id) => {
    setDeleteModal({ isOpen: true, ruleId: id, isLoading: false });
  };

  const confirmDeleteRule = () => {
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    setRules((prev) => prev.filter((r) => r.id !== deleteModal.ruleId));
    setDeleteModal({ isOpen: false, ruleId: null, isLoading: false });
    Toast.success('Rule removed.');
  };

  const cancelDeleteRule = () => {
    setDeleteModal({ isOpen: false, ruleId: null, isLoading: false });
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${LEGAL_PAGES.apiUrl}/cookie-block-rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
        body: JSON.stringify({ rules }),
      });
      const result = await res.json();
      if (result.success) {
        Toast.success('Rules saved!');
        if (Array.isArray(result.data)) setRules(result.data);
      } else {
        Toast.error('Failed to save rules.');
      }
    } catch (e) {
      Toast.error('An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  // Group rules by consent type for display
  const grouped = CONSENT_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = rules.filter((r) => r.consent === opt.value);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {rules.length === 0 ? (
        <p className="text-sm text-[#6F6F7B] py-4 text-center">
          No rules added yet. Click "Add a Rule" to get started.
        </p>
      ) : (
        <div className="space-y-6">
          {CONSENT_OPTIONS.map((opt) =>
            grouped[opt.value].length > 0 ? (
              <div key={opt.value}>
                <h4 className="text-sm font-semibold text-[#17171C] mb-3">{opt.label}</h4>
                <div className="space-y-2">
                  {grouped[opt.value].map((rule) => (
                    <div key={rule.id} className="flex items-center gap-3">
                      {/* Service dropdown */}
                      <div className="w-64">
                        <ServiceDropdown
                          value={rule.service}
                          onChange={(val) => updateRule(rule.id, 'service', val)}
                          services={AVAILABLE_SERVICES}
                          placeholder="Service"
                        />
                      </div>

                      {/* Consent type */}
                      <div className="w-52">
                        <ConsentSelect
                          value={rule.consent}
                          onChange={(val) => updateRule(rule.id, 'consent', val)}
                        />
                      </div>

                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => removeRule(rule.id)}
                        className="text-[#6F6F7B] cursor-pointer hover:text-red-500 transition p-1"
                        title="Remove rule"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={cancelDeleteRule}
        onConfirm={confirmDeleteRule}
        title="Delete Rule"
        message="Are you sure you want to delete this rule? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteModal.isLoading}
      />

      {/* Add a Rule button */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={addRule}
          className="inline-flex items-center gap-2 bg-[#17171C] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#3f3f46] transition"
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          ADD A RULE
        </button>

        {rules.length > 0 && (
          <button
            type="button"
            onClick={saveRules}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#7C3BED] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Rules'}
          </button>
        )}
      </div>
    </div>
  );
};

export default RulesTab;